<template>
  <div class="forms-container">
    <div v-if="loading" class="loading-state">
      <BaseMiniSpinner />
    </div>

    <div v-else-if="forms.length === 0" class="empty-state">
      {{ $t("message.forms_page.no_forms") }}
    </div>

    <table v-else class="forms-table">
      <thead>
        <tr>
          <th class="col-name">{{ $t("message.forms_page.form_name") }}</th>
          <th class="col-fields">{{ $t("message.forms_page.fields") }}</th>
          <th class="col-actions">
            {{ $t("message.forms_page.actions") || "Actions" }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="form in forms" :key="form.id" class="form-row">
          <td class="col-name">
            <div class="form-name-cell">
              <h4>{{ form.name }}</h4>
              <p class="form-uuid">{{ form.uuid }}</p>
            </div>
          </td>
          <td class="col-fields">
            <span class="fields-badge">{{
              form.definition.fields.length
            }}</span>
          </td>
          <td class="col-actions">
            <div class="action-buttons">
              <button
                class="btn-action btn-primary"
                @click="$emit('copy-link', form)"
                title="Copy form link"
              >
                {{ $t("message.forms_page.copy_link") }}
              </button>
              <button
                class="btn-action btn-secondary"
                @click="$emit('view-submissions', form)"
                title="View submissions"
              >
                {{ $t("message.forms_page.view_submissions") }}
              </button>
              <button
                class="btn-action btn-secondary"
                @click="$emit('edit-form', form)"
                title="Edit form"
              >
                {{ $t("message.forms_page.edit") }}
              </button>
              <button
                class="btn-action btn-danger"
                @click="$emit('delete-form', form)"
                title="Delete form"
              >
                {{ $t("message.forms_page.delete") }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import BaseMiniSpinner from "@/components/global/_base-mini-spinner.vue";

export default {
  name: "FormsListTable",
  components: {
    BaseMiniSpinner
  },
  props: {
    forms: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style lang="scss" scoped>
.forms-container {
  margin-top: 20px;
}

.forms-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  thead {
    background: #f5f5f5;
    border-bottom: 2px solid #e0e0e0;
  }

  th {
    padding: 14px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
    font-size: 14px;
  }

  .form-row {
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f9f9f9;
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  .col-name {
    width: 40%;
  }

  .col-fields {
    width: 15%;
    text-align: center;
  }

  .col-actions {
    width: 45%;
  }

  .form-name-cell {
    h4 {
      margin: 0 0 4px 0;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      word-break: break-word;
    }

    .form-uuid {
      margin: 0;
      font-size: 11px;
      color: #999;
      font-family: monospace;
      word-break: break-all;
    }
  }

  .fields-badge {
    display: inline-block;
    background: #e3f2fd;
    color: #1976d2;
    padding: 4px 10px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 12px;
  }

  .action-buttons {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .btn-action {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s ease;
    white-space: nowrap;

    &.btn-primary {
      background: #4caf50;
      color: white;

      &:hover {
        background: #45a049;
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
      }
    }

    &.btn-secondary {
      background: #e0e0e0;
      color: #333;

      &:hover {
        background: #d0d0d0;
      }
    }

    &.btn-danger {
      background: #ff6b6b;
      color: white;

      &:hover {
        background: #ee5a52;
      }
    }
  }

  @media (max-width: 1024px) {
    .col-name {
      width: 35%;
    }

    .col-actions {
      width: 50%;
    }

    .btn-action {
      padding: 5px 10px;
      font-size: 11px;
    }
  }

  @media (max-width: 768px) {
    font-size: 12px;

    th,
    td {
      padding: 10px 8px;
    }

    .col-name {
      width: 50%;
    }

    .col-fields {
      display: none;
    }

    .col-actions {
      width: 50%;
    }

    .form-name-cell h4 {
      font-size: 13px;
    }

    .form-name-cell .form-uuid {
      display: none;
    }

    .action-buttons {
      gap: 4px;
    }

    .btn-action {
      padding: 4px 8px;
      font-size: 10px;
      flex: 1;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px;
}
</style>
